/**
 * KAN-865: поля строки CRM одного клиента формой (подпись над значением) для
 * панели карточки канбан-доски. Те же редакторы ячеек, что у таблицы CRM
 * (CrmFieldEditors), и то же сохранение (useCrmRowEdit): правка меняет кеш
 * строки и обновляет доску, таблицу и блок внимания. Заказы (как в таблице)
 * только тем, кому открыт API заказов (KAN-811).
 * KAN-876: под телефоном кнопка "Позвонить" (ManagerCallButton: без права
 * ссылка tel:, без номера кабинета выключена); пустой телефон: без кнопки.
 */
import type { ReactNode } from 'react';
import { ManagerCallButton } from '@/components/clients/ManagerCallButton';
import { NextBotTouchNote } from '@/components/clients/NextBotTouchNote';
import { LeadTemperatureBadge } from '@/components/leads/LeadTemperatureBadge';
import { OrderComposeDialog } from '@/components/orders/OrderComposeDialog';
import { OrderViewDialog } from '@/components/orders/OrderViewDialog';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { useCrmOrderDialogs } from '@/hooks/useCrmOrderDialogs';
import { useCrmRowEdit } from '@/hooks/useCrmRowEdit';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { ANY_UUID } from '@/lib/managerAccess';
import type { CrmTableRow } from '@/lib/crmTable';
import type { CrmAttribute, CrmFunnelStage, CrmRowPatch } from '@/types/crm';
import { CrmDateCell, CurrentOrderCell, OrderActionButton, OrdersCell, ReasonCell } from '../CrmCells';
import {
  CrmAttributeField,
  CrmCommentField,
  CrmCompanyField,
  CrmContactField,
  CrmManagerField,
  CrmNextContactField,
  CrmStageField,
  CrmTelegramField,
  CrmTextField,
} from '../CrmFieldEditors';

interface CrmClientDetailsProps {
  item: CrmTableRow;
  stages: readonly CrmFunnelStage[];
  attributes: readonly CrmAttribute[];
  /** Кнопки действий строки (useCrmRowActions.render). */
  actions: ReactNode;
  /** KAN-876: размер кнопки "Позвонить": lg в приложении на телефоне. */
  callSize?: 'sm' | 'lg';
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="mb-0.5 text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-gray-800">{children}</dd>
    </div>
  );
}

export function CrmClientDetails({
  item,
  stages,
  attributes,
  actions,
  callSize = 'sm',
}: CrmClientDetailsProps): JSX.Element {
  const { row } = item;
  const phone = row.phone?.trim() ?? '';
  const { save, managers } = useCrmRowEdit();
  const timeZone = useOrgTimezone();
  const orders = useCrmOrderDialogs();
  const canOrders = useCanCallApi('GET', `/api/v1/orders/${ANY_UUID}/`);
  const rowSave = (patch: CrmRowPatch) => save(row.client_id, patch);

  return (
    <div className="space-y-4" data-testid="crm-client-details">
      {(actions || canOrders) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="crm-client-actions">
          {actions}
          {canOrders && (
            <OrderActionButton
              order={row.current_order}
              creating={orders.creatingFor === row.client_id}
              onOpen={orders.openCompose}
              onCreate={() => orders.startOrder(row.client_id)}
            />
          )}
        </div>
      )}
      <dl className="grid grid-cols-1 gap-3">
        <Field label="Телефон">
          <CrmTextField row={row} field="phone" label="Телефон" save={rowSave} />
          {phone && (
            <ManagerCallButton
              clientId={row.client_id}
              phone={phone}
              size={callSize}
              showPhone={false}
              targetMode="eager"
              testId="crm-client-call"
              className="mt-1"
            />
          )}
        </Field>
        <Field label="Контакт">
          <CrmContactField row={row} save={rowSave} clientHref={null} />
          <LeadTemperatureBadge level={row.lead_temperature} className="mt-1" />
        </Field>
        <Field label="Причина">
          <div className="mb-1">
            <CrmDateCell item={item} />
          </div>
          <ReasonCell row={row} timeZone={timeZone} />
          {row.next_bot_touch && (
            <div className="mt-1">
              <NextBotTouchNote clientId={row.client_id} touch={row.next_bot_touch} variant="compact" />
            </div>
          )}
        </Field>
        <Field label="Этап">
          <CrmStageField row={row} stages={stages} save={rowSave} />
        </Field>
        <Field label="Организация">
          <CrmCompanyField row={row} save={rowSave} />
        </Field>
        <Field label="Город">
          <CrmTextField row={row} field="city" label="Город" save={rowSave} />
        </Field>
        <Field label="Telegram">
          <CrmTelegramField row={row} save={rowSave} />
        </Field>
        <Field label="Менеджер">
          <CrmManagerField row={row} managers={managers} save={rowSave} />
        </Field>
        <Field label="Следующая связь">
          <CrmNextContactField row={row} save={rowSave} timeZone={timeZone} />
        </Field>
        <Field label="Комментарий">
          <CrmCommentField row={row} save={rowSave} />
        </Field>
        {attributes.map((attribute) => (
          <Field key={`attr-${attribute.id}`} label={attribute.name}>
            <CrmAttributeField row={row} attribute={attribute} save={rowSave} />
          </Field>
        ))}
        <Field label="Заказы">
          <OrdersCell orders={row.orders} onOpen={canOrders ? orders.openView : undefined} />
        </Field>
        <Field label="Текущий заказ">
          <CurrentOrderCell order={row.current_order} onOpen={canOrders ? orders.openCompose : undefined} />
        </Field>
      </dl>
      <OrderViewDialog orderId={orders.viewOrderId} onClose={orders.closeView} />
      <OrderComposeDialog orderId={orders.composeOrderId} onClose={orders.closeCompose} />
    </div>
  );
}
